import { IconService } from '@ts/services/iconService';
import { useEffect, useRef, useState } from 'preact/hooks';
import { url } from '@ts/utils/url';
import { FunctionComponent, JSX } from 'preact';
import { render } from 'preact/compat';

type SVGComponent = FunctionComponent<
  JSX.HTMLAttributes<SVGElement> & {
    width?: number;
    height?: number;
  }
>;

type Dimensions = {
  width: number;
  height: number;
};

type SvgData = Dimensions & {
  svgString: string;
  title: string;
  isImg: boolean;
};

type IconProps = Dimensions & {
  iconName: string;
  folderName?: string;
  wrapperClass?: string;
  title?: string;
  isImg?: boolean;
  iconService?: IconService;
};

export const parseSvg = ({
  svgString,
  width,
  height,
  title = '',
  isImg = true,
}: SvgData) => {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;

  svgElement.removeAttribute('xmlns');
  svgElement.setAttribute('class', 'bo-icon__item');
  svgElement.setAttribute('width', `${width}`);
  svgElement.setAttribute('height', `${height}`);
  if (isImg) {
    svgElement.setAttribute('role', 'img');
    svgElement.setAttribute('aria-label', title);
  } else {
    svgElement.setAttribute('aria-hidden', 'true');
  }

  return svgElement;
};

export const Icon = ({
  iconName,
  width,
  height,
  folderName = 'icons',
  wrapperClass = 'bo-icon',
  title = '',
  isImg = true,
  iconService = new IconService(),
}: IconProps) => {
  const iconRef = useRef<HTMLDivElement>(null);
  const [SvgComponent, setSvgComponent] = useState<SVGComponent>();

  const isStorybook = url.getPortalUrl() === 'https://storybookurl.com';

  const loadStorybookSvg = async () => {
    try {
      const { default: SvgComponent } = await import(
        `../../../../assets/${folderName}/${iconName}.svg`
      );

      setSvgComponent(() => SvgComponent);
    } catch (err) {
      console.error('Failed to load SVG: ', err);
    }
  };

  const getIcon = async (service: IconService) => {
    try {
      const svgString = await service.getRegisteredIcon(iconName);

      const parsedSvgIcon = parseSvg({
        svgString,
        width,
        height,
        title,
        isImg,
      });

      if (parsedSvgIcon && iconRef.current) {
        iconRef.current.innerHTML = '';
        iconRef.current.appendChild(parsedSvgIcon);
      }
    } catch (error) {
      if (error instanceof Error)
        console.error(`Error getting icon: ${error.message}`);
    }
  };

  useEffect(() => {
    if (isStorybook) {
      loadStorybookSvg();
    } else {
      getIcon(iconService);
    }
  }, [iconName, width, height]);

  if (isStorybook && SvgComponent)
    return (
      <>
        {wrapperClass ? (
          <div className={wrapperClass}>
            <SvgComponent width={width} height={height} />
          </div>
        ) : (
          <SvgComponent width={width} height={height} />
        )}
      </>
    );

  return <div className={wrapperClass} ref={iconRef} data-testid="icon"></div>;
};

/**
 * Insert Icon Component into the DOM
 */
export const insertIcon = (
  iconDetails: IconProps,
  iconElement: HTMLElement,
) => {
  render(
    <Icon
      iconName={iconDetails.iconName}
      width={iconDetails.width}
      height={iconDetails.height}
      isImg={iconDetails.isImg}
    />,
    iconElement,
  );
};
